import { warnAndNotify } from '@execonline-inc/error-reporting.private';
import { log } from '@execonline-inc/logging';
import { always, assertNever, noop } from '@kofno/piper';
import { Channel } from 'actioncable';
import { Maybe, fromEmpty, just, nothing } from 'maybeasy';
import { action, autorun, makeAutoObservable, observable, runInAction } from 'mobx';
import { currentUserStore } from '../../../../../CurrentUserStore';
import { createDisposableBehavior } from '../../../../../stores/DisposableBehavior';
import { cableStore } from '../../../../Chatbot/ChatStores/CableStore';
import { chatStore } from '../../../../Chatbot/ChatStores/ChatStore';
import { StreamSequencer } from '../../../../Chatbot/StreamSequencer';
import { streamedPreviewMessageDecoder } from './Decoders';
import {
  PreviewError,
  PreviewResource,
  PreviewState,
  StreamedPreview,
  StreamedPreviewMessage,
  confirmingCancel,
  confirmingCancelFromCreate,
  confirmingCancelFromPublish,
  confirmingCancelFromSaveAndEdit,
  createCollectionT,
  creating,
  discardingChanges,
  error,
  fetchLearningCollectionPreviewTemplate,
  loading,
  publishCelebration,
  publishing,
  ready,
  saveAndEditing,
  saveCelebration,
  savingFromCreating,
  savingFromPublishing,
  savingFromSaveAndEditing,
  waiting,
} from './Types';
import { chatMetadataStore } from '../../../../Chatbot/ChatStores/ChatMetadataStore';

class LearningCollectionPreviewStore {
  @observable state: PreviewState = waiting();
  @observable private latestDisplayedTurnUUID: string | null = null;
  private pendingPreview: Maybe<StreamedPreview> = nothing();
  private channel?: Channel;
  private previewBehavior = createDisposableBehavior();
  private sequencer = new StreamSequencer<StreamedPreviewMessage>(chatStore.isTurnNewer);

  constructor() {
    makeAutoObservable(this);
  }

  initialize(): void {
    this.previewBehavior.initialize(() =>
      autorun(() => {
        if (
          currentUserStore.currentUserState.kind === 'identified' &&
          cableStore.state.kind === 'ready'
        ) {
          runInAction(() => {
            this.state = loading();
            this.fetchPreview();
            this.setupCableSubscription();
          });
        }
      }),
    );
  }

  dispose = (): void => {
    if (this.channel) {
      this.channel.unsubscribe();
    }
    this.previewBehavior.dispose();
    this.sequencer.reset();

    chatStore.reset();
    cableStore.disconnect();
    cableStore.reconnect();
    chatStore.initialize();
    this.initialize();
  };

  private shouldProcessPreview = (turnUUID: string): boolean => {
    const isCurrentTurn = this.latestDisplayedTurnUUID === turnUUID;
    const isNewer = chatStore.isTurnNewer(turnUUID, this.latestDisplayedTurnUUID);

    return isCurrentTurn || isNewer;
  };

  private handleEndOfStream = (): void => {
    this.pendingPreview.cata({
      Nothing: () =>
        warnAndNotify('StreamEndParseError', 'End of stream arrived without a preview.', {}),
      Just: this.applyPreview,
    });
    this.pendingPreview = nothing();
  };

  private applyPreview = (preview: StreamedPreview): void => {
    if (this.state.kind !== 'ready') return;

    if (this.shouldProcessPreview(preview.turnUUID)) {
      const updatedResource = {
        ...this.state.resource,
        payload: {
          ...this.state.resource.payload,
          name: fromEmpty(preview.name).orElse(always(this.state.resource.payload.name)),
          description: fromEmpty(preview.description).orElse(
            always(this.state.resource.payload.description),
          ),
          recommended_product_collection: {
            ...this.state.resource.payload.recommended_product_collection,
            experiences: fromEmpty(preview.recommendedProgramFamilyIds).getOrElseValue(
              this.state.resource.payload.recommended_product_collection.experiences,
            ),
          },
        },
      };

      this.handleIncomingToReadyTransition(updatedResource, preview.turnUUID);
    } else {
      log('Preview update out of order', {
        turnUUID: preview.turnUUID,
        latestDisplayedTurnUUID: this.latestDisplayedTurnUUID,
        allTurns: chatStore.turnUUIDs,
      });
    }
  };

  private setupCableSubscription = (): void => {
    if (cableStore.state.kind !== 'ready') return;

    this.channel = cableStore.state.cable.subscriptions.create(
      { channel: 'EnterprisePortal::PreviewChannel', chat_id: cableStore.state.correlationId },
      {
        connected() {
          log('Connected to PreviewChannel');
        },
        disconnected() {
          log('Disconnected from PreviewChannel');
        },
        received: (data: string) => {
          streamedPreviewMessageDecoder.decodeJson(data).cata({
            Ok: (message) =>
              this.sequencer
                .accept(message.turnUUID, message.seq, message)
                .forEach(this.handlePreviewMessage),
            Err: (error) =>
              warnAndNotify('StreamedMessageParseError', 'Failed to parse chat stream message.', {
                error,
              }),
          });
        },
      },
    );
  };

  private handlePreviewMessage = (message: StreamedPreviewMessage): void => {
    switch (message.type) {
      case 'preview':
        this.pendingPreview = just(message);
        break;
      case 'end-of-stream':
        this.handleEndOfStream();
        break;
      default:
        assertNever(message);
    }
  };

  private fetchPreview = (): void => {
    fetchLearningCollectionPreviewTemplate().fork(
      noop, // Error handled by decoder
      (resource) =>
        runInAction(() => {
          this.state = ready(resource, nothing(), this.state);
        }),
    );
  };

  @action
  startPublishing = () => {
    if (this.state.kind === 'ready') {
      this.state = publishing(this.state);
    }
  };

  @action
  startCreating = () => {
    if (this.state.kind === 'ready') {
      this.state = creating(this.state);
    }
  };

  @action
  startSaveAndEditing = () => {
    if (this.state.kind === 'ready') {
      this.state = saveAndEditing(this.state);
    }
  };

  @action
  save = () => {
    switch (this.state.kind) {
      case 'publishing': {
        const resource = this.state.resource;
        this.sendSaveNewRequest(this.state.kind, resource, this.state.internalName);
        this.state = savingFromPublishing(this.state);
        break;
      }
      case 'creating': {
        const resource = this.state.resource;
        this.sendSaveNewRequest(this.state.kind, resource, this.state.internalName);
        this.state = savingFromCreating(this.state);
        break;
      }
      case 'save-and-editing': {
        const resource = this.state.resource;
        this.sendSaveNewRequest(this.state.kind, resource, this.state.internalName);
        this.state = savingFromSaveAndEditing(this.state);
        break;
      }
      case 'waiting':
      case 'ready':
      case 'recommendations-incoming':
      case 'confirming-cancel':
      case 'confirming-cancel-from-publish':
      case 'confirming-cancel-from-create':
      case 'confirming-cancel-from-save-and-edit':
      case 'error':
      case 'loading':
      case 'saving-from-publishing':
      case 'saving-from-creating':
      case 'saving-from-save-and-editing':
      case 'publish-celebration':
      case 'save-celebration':
      case 'discarding-changes':
        break;
      default:
        assertNever(this.state);
    }
  };

  private sendSaveNewRequest = (
    kind: 'publishing' | 'creating' | 'save-and-editing',
    resource: PreviewResource,
    internalName: Maybe<string>,
  ) => {
    createCollectionT(resource, kind, internalName).fork(this.handleError, this.handleSuccess);
  };

  @action
  showCancelConfirmation = () => {
    switch (this.state.kind) {
      case 'ready':
        this.state = confirmingCancel(this.state);
        break;
      case 'waiting':
        break;
      case 'publishing':
        this.state = confirmingCancelFromPublish(this.state);
        break;
      case 'creating':
        this.state = confirmingCancelFromCreate(this.state);
        break;
      case 'save-and-editing':
        this.state = confirmingCancelFromSaveAndEdit(this.state);
        break;
      case 'confirming-cancel':
      case 'confirming-cancel-from-publish':
      case 'confirming-cancel-from-create':
      case 'confirming-cancel-from-save-and-edit':
      case 'recommendations-incoming':
      case 'error':
      case 'loading':
      case 'saving-from-publishing':
      case 'saving-from-creating':
      case 'saving-from-save-and-editing':
      case 'publish-celebration':
      case 'save-celebration':
      case 'discarding-changes':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  wait = () => {
    switch (this.state.kind) {
      case 'confirming-cancel':
        this.state = ready(this.state.resource, this.state.internalName, this.state);
        break;
      case 'confirming-cancel-from-publish':
        this.state = publishing(this.state);
        break;
      case 'confirming-cancel-from-create':
        this.state = creating(this.state);
        break;
      case 'confirming-cancel-from-save-and-edit':
        this.state = saveAndEditing(this.state);
        break;
      case 'publishing':
      case 'creating':
      case 'save-and-editing':
        this.state = ready(this.state.resource, this.state.internalName, this.state);
        break;
      case 'ready':
      case 'recommendations-incoming':
      case 'waiting':
      case 'error':
      case 'loading':
      case 'saving-from-publishing':
      case 'saving-from-creating':
      case 'saving-from-save-and-editing':
      case 'publish-celebration':
      case 'save-celebration':
      case 'discarding-changes':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  addInternalName = (internalName: string) => {
    switch (this.state.kind) {
      case 'confirming-cancel':
      case 'confirming-cancel-from-publish':
      case 'confirming-cancel-from-create':
      case 'confirming-cancel-from-save-and-edit':
      case 'waiting':
      case 'error':
      case 'loading':
      case 'publish-celebration':
      case 'save-celebration':
      case 'creating':
      case 'publishing':
      case 'save-and-editing':
      case 'saving-from-creating':
      case 'saving-from-publishing':
      case 'saving-from-save-and-editing':
      case 'recommendations-incoming':
      case 'discarding-changes':
        break;
      case 'ready':
        this.state = ready(this.state.resource, just(internalName), this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  discardChanges = () => {
    switch (this.state.kind) {
      case 'confirming-cancel':
      case 'confirming-cancel-from-publish':
      case 'confirming-cancel-from-create':
      case 'confirming-cancel-from-save-and-edit':
        this.state = discardingChanges('/home');
        break;
      case 'ready':
      case 'recommendations-incoming':
      case 'publishing':
      case 'creating':
      case 'save-and-editing':
      case 'error':
      case 'loading':
      case 'waiting':
      case 'saving-from-publishing':
      case 'saving-from-creating':
      case 'saving-from-save-and-editing':
      case 'publish-celebration':
      case 'save-celebration':
      case 'discarding-changes':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  private handleSuccess = (id: number) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'ready':
      case 'recommendations-incoming':
      case 'error':
      case 'publishing':
      case 'creating':
      case 'save-and-editing':
      case 'confirming-cancel':
      case 'confirming-cancel-from-publish':
      case 'confirming-cancel-from-create':
      case 'confirming-cancel-from-save-and-edit':
        break;
      case 'saving-from-publishing':
        this.state = publishCelebration();
        break;
      case 'saving-from-creating':
        this.state = saveCelebration();
        break;
      case 'saving-from-save-and-editing':
        switch (chatMetadataStore.assistantRole) {
          case 'default':
            this.state = discardingChanges(`/learning_collections/${id}/edit/preview`);
            break;
          case 'coach':
            this.state = discardingChanges(`/learning_collections/${id}/edit/describe`);
            break;
          default:
            assertNever(chatMetadataStore.assistantRole);
        }
        break;
      case 'publish-celebration':
      case 'save-celebration':
      case 'discarding-changes':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  private handleError = (e: PreviewError) => {
    switch (this.state.kind) {
      case 'saving-from-publishing':
      case 'saving-from-creating':
      case 'saving-from-save-and-editing':
        this.state = error(e);
        break;
      default:
        break;
    }
  };

  @action
  filterStillAvailableRecommendations = (stillAvailableRecommendationIds: number[]) => {
    if (this.state.kind === 'ready') {
      const updatedResource = {
        ...this.state.resource,
        payload: {
          ...this.state.resource.payload,
          recommended_product_collection: {
            ...this.state.resource.payload.recommended_product_collection,
            experiences: stillAvailableRecommendationIds,
          },
        },
      };

      this.handleIncomingToReadyTransition(updatedResource);
    }
  };

  private handleIncomingToReadyTransition(updatedResource: PreviewResource, turnUUID?: string) {
    runInAction(() => {
      this.state = {
        kind: 'recommendations-incoming',
        resource: updatedResource,
        internalName: this.state.kind === 'ready' ? this.state.internalName : nothing(),
      };
    });
    setTimeout(() => {
      runInAction(() => {
        if (this.state.kind === 'recommendations-incoming') {
          this.state = {
            kind: 'ready',
            resource: this.state.resource,
            internalName: this.state.internalName,
          };

          if (turnUUID) {
            this.latestDisplayedTurnUUID = turnUUID;
          }
        }
      });
    }, 2000);
  }
}

const previewStore = new LearningCollectionPreviewStore();
previewStore.initialize();

export { previewStore };
